/**
 * The alert recipients of a line (docs/plans/oncall-line-plan.md 2.3
 * alert_emails, 3 D9; WP-11): who gets the no-PHI emails (answered calls,
 * missed calls, a failed heartbeat, a locked PIN). Up to ten addresses. An
 * address is checked as it is added, so a mistyped recipient never waits
 * silently to become a missed alert (the server refuses one too, plan 16
 * WP-06). With no recipients no alert email goes anywhere, so the editor
 * says so.
 */
import { useState } from 'react';
import type { KeyboardEvent } from 'react';
import { Mail, Plus, X } from 'lucide-react';
import { fieldCls, secondaryBtnCls } from '../../../ui/styles';
import { MAX_ALERT_EMAILS } from './huntMath';
import { isEmail, normalizeEmail } from './settingsForm';

export interface AlertEmailsEditorProps {
  emails: string[];
  onChange: (next: string[]) => void;
  /** A message from the form's validation, shown under the list. */
  error?: string;
  disabled?: boolean;
  /** Told what is typed but not yet added, so the page can refuse to save past it. */
  onDraftChange?: (draft: string) => void;
}

export default function AlertEmailsEditor({ emails, onChange, error, disabled = false, onDraftChange }: AlertEmailsEditorProps) {
  const [draft, setDraftState] = useState('');
  const setDraft = (value: string) => {
    setDraftState(value);
    onDraftChange?.(value);
  };
  const [draftError, setDraftError] = useState<string | null>(null);
  const full = emails.length >= MAX_ALERT_EMAILS;

  const add = () => {
    const email = normalizeEmail(draft);
    if (!email) return;
    if (!isEmail(email)) {
      setDraftError('That is not an email address.');
      return;
    }
    if (emails.map(normalizeEmail).includes(email)) {
      setDraftError('That address is already on the list.');
      return;
    }
    if (full) {
      setDraftError(`At most ${MAX_ALERT_EMAILS} addresses.`);
      return;
    }
    onChange([...emails, email]);
    setDraft('');
    setDraftError(null);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      add();
    }
  };

  return (
    <div data-testid="oncall-alert-emails">
      {emails.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label="Alert recipients">
          {emails.map((email) => (
            <li
              key={email}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-gray-300 bg-gray-50 py-0.5 pl-2.5 pr-1 text-xs font-medium text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
            >
              <Mail className="h-3 w-3 flex-shrink-0 text-gray-400" aria-hidden="true" />
              <span className="min-w-0 truncate">{email}</span>
              <button
                type="button"
                onClick={() => onChange(emails.filter((e) => e !== email))}
                disabled={disabled}
                aria-label={`Remove ${email}`}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors duration-150 hover:bg-gray-200 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:hover:bg-gray-600 dark:hover:text-gray-100 sm:h-5 sm:w-5"
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-amber-700 dark:text-amber-300" data-testid="oncall-alert-emails-empty">
          No recipients: alert emails for this line go nowhere.
        </p>
      )}

      <div className="mt-2 flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1 sm:max-w-sm">
          <label htmlFor="oncall-alert-email-new" className="sr-only">
            Add an alert email address
          </label>
          <input
            id="oncall-alert-email-new"
            type="email"
            inputMode="email"
            autoComplete="off"
            value={draft}
            placeholder="name@westclinicalresearch.com"
            disabled={disabled || full}
            onChange={(e) => {
              setDraft(e.target.value);
              if (draftError) setDraftError(null);
            }}
            onKeyDown={onKeyDown}
            aria-invalid={draftError ? true : undefined}
            aria-describedby={draftError ? 'oncall-alert-email-error' : undefined}
            className={`${fieldCls} w-full`}
          />
        </div>
        <button
          type="button"
          onClick={add}
          disabled={disabled || full || !draft.trim()}
          data-testid="oncall-alert-email-add"
          className={`${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          Add
        </button>
      </div>
      {draftError && (
        <p id="oncall-alert-email-error" className="mt-1 text-xs text-red-600 dark:text-red-400">
          {draftError}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        {emails.length} of {MAX_ALERT_EMAILS}. Alerts carry no caller number and no transcript, only a link.
      </p>
    </div>
  );
}
